import React, { useState, useRef } from 'react';
import classes from './AudioVisualizer.module.css';
import { numberToSeconds } from '../../../utils/miscUtils';

interface AudioVisualizerProps {
  audioStream: MediaStream;
}

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ audioStream }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [canvasContext, setCanvasContext] = useState<CanvasRenderingContext2D | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  React.useEffect(() => {
    if (canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      if (context) {
        setCanvasContext(context);
      }
    }
  }, []);

  React.useEffect(() => {
    if (!canvasContext) return;

    const audioContext = new window.AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(audioStream);
    const gainNode = audioContext.createGain(); // Create a gain node

    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    source.connect(analyser);
    analyser.connect(gainNode);
    gainNode.connect(audioContext.destination);

    gainNode.gain.value = 0;

    let animationFrameId: number;

    setStartTime(performance.now());

    const drawVisualizer = () => {
      analyser.getByteFrequencyData(dataArray);

      if (canvasContext) {
        canvasContext.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height);
        const centerY = canvasRef.current!.height / 2;
        const barWidth = canvasRef.current!.width / bufferLength - 1;
        const maxBarHeight = canvasRef.current!.height / 2;
        const scale = maxBarHeight / 128;

        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const barHeight = dataArray[i] * scale;
          canvasContext.fillStyle = '#0B1B5C';
          canvasContext.fillRect(x, centerY - barHeight, barWidth, barHeight * 2);
          x += barWidth + 3;
        }
        animationFrameId = requestAnimationFrame(drawVisualizer);
      }
    };

    drawVisualizer();

    return () => {
      cancelAnimationFrame(animationFrameId);
      analyser.disconnect();
      audioContext.close();
    };
  }, [audioStream, canvasContext]);

  React.useEffect(() => {
    if (startTime !== null) {
      const intervalId = setInterval(() => {
        const currentTime = performance.now();
        const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
        setElapsedTime(elapsedSeconds);
      }, 1000); // Update every second

      return () => clearInterval(intervalId);
    }
  }, [startTime]);

  return (
    <div className={classes.audioVisualizer}>
      <canvas ref={canvasRef} className={classes.visualizerCanvas} />
      <div className={classes.timer}>{numberToSeconds(elapsedTime)}</div>
    </div>
  );
};

export { AudioVisualizer };
